import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { MarketingBannerRow } from "../MarketingBanners/types";

import { Button } from "../Button";
import { scaleDimensions } from "../MarketingBanners/CreativePreview/helpers";
import { HTMLAdPreview } from "../MarketingBanners/CreativePreview/HTMLAdPreview";
import { ImageBannerPreview } from "../MarketingBanners/CreativePreview/ImageBannerPreview";
import { UnknownAssetPreview } from "../MarketingBanners/CreativePreview/UnknownAssetPreview";
import { SkeletonLoader } from "../SkeletonLoader";

type CardThumbnailProps = {
  banner: MarketingBannerRow;
  /** Locale-aware label for the unknown-asset fallback (defaults to English). */
  unknownAssetLabel?: string;
  onConfigure?: () => void;
};

export function CardThumbnail({
  banner,
  unknownAssetLabel,
  onConfigure,
}: CardThumbnailProps) {
  const { t } = useTranslation();
  const configureAssetLabel = t(
    "marketingCard_configureAsset",
    "Configure asset",
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({
    width: 300,
    height: 250,
  });
  const [isLoading, setIsLoading] = useState(true);

  const hideLoadingSkeleton = () => setIsLoading(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setContainerSize({
        width: Math.max(Math.round(width), 1),
        height: Math.max(Math.round(height), 1),
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const originalDimensions = { width: banner.width, height: banner.height };
  const { width, height, scale } = scaleDimensions(
    originalDimensions,
    containerSize,
  );

  const renderPreview = (): ReactNode => {
    switch (banner.type) {
      case "html_ad":
        return (
          <HTMLAdPreview
            banner={banner}
            width={width}
            height={height}
            scale={scale}
            onLoad={hideLoadingSkeleton}
          />
        );
      case "image_banner":
        return (
          <ImageBannerPreview banner={banner} onLoad={hideLoadingSkeleton} />
        );
      default:
        return null;
    }
  };

  const preview = renderPreview();

  return (
    <div className="group/card relative aspect-[616/512] overflow-hidden rounded-lg bg-surface-secondary">
      <div
        ref={containerRef}
        className="absolute inset-6 flex items-center justify-center overflow-hidden rounded-lg"
        aria-label={banner.name}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <SkeletonLoader width={width} height={height} />
          </div>
        )}
        {preview ?? <UnknownAssetPreview label={unknownAssetLabel} />}
      </div>
      <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/60 opacity-0 transition-opacity duration-200 group-hover/card:opacity-100">
        <Button variant="brand" size="lg" onClick={onConfigure}>
          {configureAssetLabel}
        </Button>
      </div>
    </div>
  );
}
